import { SpotlightTourStep } from "components/ProductAnnouncement/SpotlightTour";
import { SCOUT_CITATION_EXPLAINER_ANCHOR_ID } from "components/Submission/ScoutCitationExplainer";
import { SubmissionStepType } from "generated-graphql/graphql";

/**
 * DOM id for a step's rail row. Derived from the step type so the rail and the
 * tour cannot drift — renaming a step's label leaves the anchor intact.
 */
export function wasteStepAnchorId(step: SubmissionStepType): string {
  return `waste-step-${step.toLowerCase().replace(/_/g, "-")}`;
}

export const WASTE_STEPPER_MODAL_COPY = {
  headline: "Submissions are now step by step",
  subheadline: "Same filings, one screen at a time",
  primaryLabel: "Got it",
  reopenHint: "You can always access this under Help",
} as const;

export const WASTE_STEPPER_TOUR_MODAL_COPY = {
  headline: "Take a quick tour",
  subheadline: "See what each step covers",
  primaryLabel: "Take the tour",
  reopenHint: "You can always access this under Help",
} as const;

type WasteStepperTourStepConfig = {
  step: SubmissionStepType;
  title: string;
  body: string;
};

/**
 * Walks the rail top to bottom. Reviewer Preparation is skipped — it is the one
 * step with no customer-facing copy of its own. Document Preparation and
 * Submit & File restate their customer-facing step headers, so the tour and its
 * destination agree.
 */
export const WASTE_STEPPER_TOUR_STEPS: WasteStepperTourStepConfig[] = [
  {
    step: SubmissionStepType.Understand,
    title: "Why this filing is required",
    body: "See what this submission is, and the regulation behind the requirement.",
  },
  {
    step: SubmissionStepType.PrepareDocument,
    title: "Encamp prepares the document",
    body: "We prepare and attach your submission documents for your review.",
  },
  {
    step: SubmissionStepType.ReviewAndSign,
    title: "Review & Sign",
    body: "See what Encamp prepared, then review and sign off on the submission.",
  },
  {
    step: SubmissionStepType.SubmitAndFile,
    title: "Encamp files the submission",
    body: "View any filing details and keep track of payment(s).",
  },
];

/**
 * Extra stop for Scout tenants, slotted in right after the Understand rail row
 * so the walk stays in one place: the rail row, then the button inside the step
 * it just described. Its anchor lives in the step body rather than the rail, so
 * it only applies while the Understand step is the one on screen.
 */
export const SCOUT_CITATION_TOUR_STEP: SpotlightTourStep = {
  anchorId: SCOUT_CITATION_EXPLAINER_ANCHOR_ID,
  title: "Ask Scout",
  body: "The regulation and requirements can be explained in-context by Scout.",
  placement: "bottom",
};

/** The rail sits in the left sidebar, so every popover hangs off its right edge. */
export function toSpotlightSteps(configs: WasteStepperTourStepConfig[]): SpotlightTourStep[] {
  return configs.map(({ step, title, body }) => ({
    anchorId: wasteStepAnchorId(step),
    title,
    body,
    placement: "right",
  }));
}
